import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";
import { handleStaleResponse } from "../lib/handle_stale_response";
import { autosaveDraft, cancel as cancelAutosave } from "../components/lib/autosave_draft";

export default class extends Controller {
  static targets = ["option", "submitButton"];
  static values = {
    submitUrl: String,
    draftUrl: String,
  };

  connect() {
    this.submitting = false;
    this._refreshState();
  }

  disconnect() {
    if (this.hasDraftUrlValue) cancelAutosave(this.draftUrlValue);
  }

  change() {
    this._refreshState();
    this._scheduleAutosave();
  }

  _refreshState() {
    if (!this.hasSubmitButtonTarget) return;
    const anyChecked = this.optionTargets.some((o) => o.checked);
    this.submitButtonTarget.disabled = !anyChecked || this.submitting;
  }

  _checkedValues() {
    return this.optionTargets.filter((o) => o.checked).map((o) => o.value);
  }

  _scheduleAutosave() {
    if (this.submitting || !this.hasDraftUrlValue) return;
    autosaveDraft({
      url: this.draftUrlValue,
      body: { submitted: { picks: this._checkedValues() } },
    });
  }

  submit(event) {
    event?.preventDefault();
    if (this.submitting || !this.hasSubmitUrlValue) return;
    if (this.hasSubmitButtonTarget && this.submitButtonTarget.disabled) return;

    this.submitting = true;
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;
    if (this.hasDraftUrlValue) cancelAutosave(this.draftUrlValue);

    const token = document.querySelector("meta[name=csrf-token]")?.content || "";
    const params = new URLSearchParams();
    this._checkedValues().forEach((v) => params.append("picks[]", v));

    fetch(this.submitUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": token,
      },
      body: params.toString(),
    })
      .then((r) => {
        if (handleStaleResponse(this, r)) return null;
        return r.text();
      })
      .then((html) => {
        if (html) Turbo.renderStreamMessage(html);
      })
      .finally(() => {
        this.submitting = false;
      });
  }

  tryAgain(event) {
    event?.preventDefault();
    if (this.submitting || !this.hasSubmitUrlValue) return;

    this.submitting = true;
    if (this.hasDraftUrlValue) cancelAutosave(this.draftUrlValue);

    const token = document.querySelector("meta[name=csrf-token]")?.content || "";

    fetch(this.submitUrlValue, {
      method: "DELETE",
      credentials: "same-origin",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": token,
      },
    })
      .then((r) => {
        if (handleStaleResponse(this, r)) return null;
        return r.text();
      })
      .then((html) => {
        if (html) Turbo.renderStreamMessage(html);
      })
      .finally(() => {
        this.submitting = false;
      });
  }
}
